import React, {memo, useEffect, useState} from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../../utils/TableIcons';
import setDate from '../../utils/setDate';
import convertToCurrency from '../../utils/convertToCurrency';
import theme from '../../styles/theme';
import _ from 'lodash';

import VisibilityIcon from '@material-ui/icons/Visibility';
import ClearIcon from '@material-ui/icons/Clear';
import CheckIcon from '@material-ui/icons/Check';
import RefreshIcon from '@material-ui/icons/Refresh';

const TrafficSourcesTable = memo(({trafficSources, variables, refetch, actions}) => {
    const [data, setData] = useState(trafficSources);
    const setIcon = (bool) => {
        if (bool) {
            return (
                <CheckIcon style={{color: theme.palette.success.main}}/>
            );
        }

        return <ClearIcon style={{color: theme.palette.error.main}}/>;
    };

    const getActions = () => {
        let actionsArr = {
            refresh: {
                icon: () => <RefreshIcon/>,
                tooltip: 'Refetch Data',
                isFreeAction: true,
                onClick: (event) => refetch()
            },
            view: {
                icon: () => <VisibilityIcon/>,
                tooltip: 'Edit Traffic Source',
                onClick: (event, rowData) => window.open(`/info/traffic-sources/${rowData.id}`)
            }
        };
        let finalArr = [];
        if (actions.length) {
            actions.forEach(item => {
                if(actionsArr[item]) {
                    finalArr.push(actionsArr[item]);
                }
            })
        }

        return finalArr;
 
    }

    useEffect(() => {
        if (variables && Object.keys(variables).length) {
            const {affiliateName, promoId, zoneId, trafficSourceName} = variables;
            let finalArr = [];
            if (trafficSourceName) {
                finalArr = data.filter(trafficSource => trafficSource.name.toLowerCase().includes(trafficSourceName.toLowerCase()));
            }
            if (affiliateName) {
                const affiliateArr = finalArr.length ? finalArr : data;
                finalArr = affiliateArr.filter(trafficSource => trafficSource.affiliate.name === affiliateName);
            }
            if (promoId) {
                const promoArr = finalArr.length ? finalArr : data;
                finalArr = promoArr.filter(trafficSource => trafficSource.legacyPromoId === parseInt(promoId));
            }
            if (zoneId) {
                const zoneArr = finalArr.length ? finalArr : data;
                finalArr = zoneArr.filter(trafficSource => trafficSource.legacyZoneId === parseInt(zoneId));
            }
            setData(finalArr);
        } else {
            setData(trafficSources);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variables])

    return (
        <MaterialTable
            title=""
            icons={TableIcons}
            columns={[
                {title: "Link", field: "link"},
                {title: "id", field: 'id', hidden: true},
                {title: "Created At", field: 'createdAt'},
                {title: "Affiliate", field: "affiliate"},
                {title: "Traffic Source", field: "trafficSource"},
                {title: "Tree", field: "tree"},
                {title: "Min Pay", field: "minPay", type: "numeric"},
                {title: "Max Pay", field: "maxPay", type: "numeric"},
                {title: "Payout Strategy", field: "payoutStrategy", headerStyle: {minWidth: '200px'}},
                {title: "Price Reject Mins", field: "priceRejectMins", headerStyle: {minWidth: '200px'},
                    render: (rowData) => {
                        const { priceRejectMins } = rowData;
                        if (!priceRejectMins.length) {
                            return (
                                <div>
                                    N/A
                                </div>
                            )
                        }

                        return (
                            <>
                                <div>
                                    {priceRejectMins.toString()}
                                </div>
                            </>
                        )
                    }
                },
                {title: "Internal", field: "internal"},
                {title: "Agg Only", field: "aggOnly"},
                {title: "MultiOffer", field: "multiOffer"},
                {title: "MultiConversion", field: "multiConversion"},
                {title: "Origin", field: "origin"},
                {title: "Channel", field: "channel"},
                {title: "Medium", field: "medium"},
                {title: "Source URL", field: "sourceURL"},
                {title: "Offer URL", field: "offerUrl"},
                {title: "Cpanel PubId", field: "cpanelPubId"},
                {title: "Cpanel ZoneId", field: "cpanelZoneId"},
                {title: "Cpanel PromoId", field: "cpanelPromoid"},
                {title: "Timeout", field: "timeout", type: "numeric"},
                {title: "Is Split Test", field: "splitTest"},
                {title: "Is List Managed", field: "isListManaged"},
                {title: "Include S3 In Source Id", field: "includeS3InSource"},
                {title: "Is Test", field: "isTest"},
                {title: "Proxy To Olp", field: "proxyToOlp"},
                {title: "Sold Feed", field: "soldFeed"},
                {title: "Unsold Feed", field: "unsoldFeed"},
            ]}
            data={data.map(trafficSource => ({
                link: <a target="_blank" rel="noopener noreferrer"
                         href={"/info/posting?sourceKey=" + trafficSource.id}>apidoc</a>,
                id: trafficSource.id,
                createdAt: setDate(trafficSource.createdAt),
                affiliate: trafficSource.affiliate.name,
                trafficSource: trafficSource.name,
                tree: trafficSource.rootPingTree,
                minPay: _.isNil(trafficSource.minimumPrice) || trafficSource.minimumPrice === '' ? 'dynamic' : convertToCurrency(trafficSource.minimumPrice),
                maxPay: trafficSource.maximumPrice || 'N/A',
                payoutStrategy: trafficSource.payoutStrategy.enum,
                priceRejectMins: trafficSource.priceRejectMins.mins,
                internal: setIcon(trafficSource.internal),
                aggOnly: setIcon(trafficSource.aggregatorOnly),
                origin: trafficSource.origin,
                medium: trafficSource.medium,
                channel: trafficSource.channel,
                timeout: trafficSource.timeout,
                sourceURL: trafficSource.sourceUrl,
                offerUrl: trafficSource.offerUrl,
                cpanelPubId: trafficSource.affiliate.legacyPublisherId,
                cpanelZoneId: trafficSource.legacyZoneId,
                cpanelPromoid: trafficSource.legacyPromoId,
                splitTest: setIcon(trafficSource.splitTest),
                isListManaged: setIcon(trafficSource.isListManageable),
                includeS3InSource: setIcon(trafficSource.includeS3InSource),
                isTest: setIcon(trafficSource.isTest),
                proxyToOlp: setIcon(trafficSource.proxyToOlp),
                soldFeed: trafficSource.soldFeed,
                unsoldFeed: trafficSource.unsoldFeed,
                multiOffer: setIcon(trafficSource.multiOffer),
                multiConversion: setIcon(trafficSource.multiConversion)
            }))}
            actions={getActions()}
            options={{
                pageSize: 20,
                pageSizeOptions: [20, 50, 100],
                headerStyle: {
                    minWidth: 'auto',
                },
                search: false,
            }}
        />
    );
});


export default TrafficSourcesTable;
